import React, { useState, useEffect } from 'react';
import Navbar from '../navbar/Navbar'; // Import Navbar component
import styles from './Layout.module.css'; // Import CSS styles
import { LayoutProvider, useLayout } from "./LayoutContext";
import NavigationMenu from '../menu/NavigationMenu';
import { User } from '../../gql/types';
import { isNative } from '../../platform/platform';
import { KeyboardProvider } from '../../contexts/KeyboardContext';
import LayoutView from './LayoutView';
import { InboxConnection } from '../inbox/types';
import Inbox from '../inbox/Inbox';
import ProfileView from '../profile/ProfileView';

export const OVERLAY_ZINDEX_CONSTANT = 990;

interface NavbarConfig {
    title: string; // Optional title for the Navbar
    showLogo?: boolean;
    hamburgerMenu?: boolean; // Whether to show a profile bubble
    inboxView?: boolean;
    showProfileBubble?: boolean;
    notificationsView?: boolean;
    onBack?: () => void; // Optional back button handler
    useCloseButton?: boolean;
}

interface ContainerConfig {
    removeTopPadding?: boolean;
    noPadding?: boolean;
}

interface FooterConfig {
    fixed?: boolean;
}

interface LayoutProps {
    baseZIndex?: number;
    children: React.ReactNode; // Content of the screen
    colorMode?: 'default' | 'gray';
    navbarConfig?: NavbarConfig; // Navbar configuration object
    footer?: React.ReactNode; // Optional footer element
    containerConfig?: ContainerConfig;
    containerClassName?: string;
    footerClassName?: string;
    noFooterPadding?: boolean;
    footerConfig?: FooterConfig;
    isHidden?: boolean;
    user?: User;
}

const GlobalOverlay: React.FC = () => {
    const { baseZIndex, isOverlayVisible, hideOverlayOnClick } = useLayout();

    if (!isOverlayVisible) return null;

    const finalZIndex = baseZIndex + OVERLAY_ZINDEX_CONSTANT;

    return (
        <div className={`${styles.overlay}`} style={{ zIndex: finalZIndex }} onClick={hideOverlayOnClick}>
            {/* Optionally render content inside the overlay */}
        </div>
    );
};

const Layout: React.FC<LayoutProps> = ({
    children,
    navbarConfig,
    baseZIndex = 0,
    containerClassName,
    containerConfig,
    footer,
    isHidden,
    user,
    footerClassName,
    noFooterPadding,
    footerConfig,
    colorMode,
}) => {
    const [overlayMenuShown, setOverlayMenuShown] = useState<boolean>(false);
    const [inboxShown, setInboxShown] = useState<boolean>(false);
    const [precomputedInboxConnection, setPrecomputedInboxConnection] = useState<InboxConnection | undefined>(undefined);

    const hideMainContent = () => {
        return isHidden || overlayMenuShown || inboxShown;
    }

    const inboxSetter = (precomputedConnection?: InboxConnection) => {
        setInboxShown(true);
        setPrecomputedInboxConnection(precomputedConnection);
    };

    return (
        <LayoutProvider baseZIndex={baseZIndex}>
            {navbarConfig &&
                navbarConfig.showProfileBubble &&
                user &&
                <ProfileView
                    onBack={() => setOverlayMenuShown(false)}
                    user={user} isHidden={isHidden || !overlayMenuShown} />}
            {navbarConfig && navbarConfig.hamburgerMenu && <NavigationMenu onCloseMenu={() => setOverlayMenuShown(false)} user={user} isHidden={isHidden || !overlayMenuShown} />}
            {navbarConfig && navbarConfig.inboxView && <Inbox onCloseInbox={() => setInboxShown(false)} user={user} isHidden={isHidden || !inboxShown} precomputedConnection={precomputedInboxConnection} />}
            {isNative() && <div className={`${styles.iosSafeAreaPadding}`} />}
            <GlobalOverlay />
            <KeyboardProvider>
                <div className={`
                    ${styles.appContainer}
                    ${hideMainContent() ? styles.hidden : ''}
                    ${colorMode === 'gray' ? styles.grayBackground : ''}`}>
                    {navbarConfig && !hideMainContent() && (
                        <Navbar
                            user={user}
                            title={navbarConfig.title}
                            showLogo={navbarConfig.showLogo}
                            openHamburgerMenu={navbarConfig.hamburgerMenu ? () => setOverlayMenuShown(true) : undefined}
                            openProfileBubble={navbarConfig.showProfileBubble ? () => setOverlayMenuShown(true) : undefined}
                            openInbox={navbarConfig.inboxView ? inboxSetter : undefined}
                            openNotifications={navbarConfig.notificationsView ? () => { } : undefined}
                            onBack={navbarConfig.onBack}
                            useCloseButton={navbarConfig.useCloseButton}
                        />
                    )}
                    <LayoutView
                        isHidden={hideMainContent()}
                        containerClassName={`${containerClassName} ${footer ? '' : styles.bottomPadding}`}
                        removeTopPadding={containerConfig?.removeTopPadding}
                        noPadding={containerConfig?.noPadding}
                    >
                        {children}
                    </LayoutView>
                    {footer && <div className={`${styles.footer} ${footerConfig?.fixed ? styles.footerFixed : ''} ${noFooterPadding ? styles.noPadding : ""} ${footerClassName}`}>{footer}</div>}
                </div>
            </KeyboardProvider>
        </LayoutProvider >
    );
};

export default Layout;
